import React, { useState, useEffect, useCallback, useMemo } from "react";
import styles from "./ReferFriendTab.module.css";
import axios from "axios";
import moment from "moment";
import { useDispatch } from "react-redux";
import { getPlayerBalance } from "../../store/slices/user";

// Use backend base URL from environment, fallback to production API
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL.replace(/\/+$/, "");
const FRONTEND_URL = process.env.REACT_APP_FRONTEND_URL.replace(/\/+$/, "");

export default function ReferFriendTab() {
  const [mainTab, setMainTab] = useState("overview"); // overview | report | friends | income
  const [incomeTab, setIncomeTab] = useState("income"); // income | incomeHistory
  const [startDate, setStartDate] = useState("2025-08-01");
  const [endDate, setEndDate] = useState("2025-08-31");
  const [inviteDetail, setInviteDetail] = useState("");
  const [withdrawDetail, setWithdrawDetail] = useState("");
  const [friendInvite, setFriendInvite] = useState([]);
  const [invitedFriends, setInvitedFriends] = useState([]);
  const [invitedFriendsSummary, setInvitedFriendsSummary] = useState({
    total: 0,
  });
  const [invitedFriendsError, setInvitedFriendsError] = useState("");
  const [loadingId, setLoadingId] = useState(null);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("info");

  const dispatch = useDispatch();

  const resolveUserId = useCallback(() => {
    try {
      const stored = localStorage.getItem("user");
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.id) return parsed.id;
      }
    } catch (_) {}
    const idFallback = localStorage.getItem("userId");
    return idFallback || null;
  }, []);

  const uid = resolveUserId();
  const referralLink = `${
    typeof window !== "undefined" ? window.location.origin : FRONTEND_URL
  }/register?friend=${uid || ""}`;

  const copy = () => {
    navigator.clipboard.writeText(referralLink).catch(() => {});
  };

  const fetchInviteSetting = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/setting/invite`);
      const list = res?.data?.data || [];
      const first = list[0] || {};
      setInviteDetail(first?.inviteDetail || "");
      setWithdrawDetail(first?.withdrawConditionDetail || "");
    } catch (e) {
      // silent
    }
  }, []);

  const fetchFriendInvite = useCallback(async () => {
    if (!uid) return;
    try {
      const res = await axios.get(
        `${API_BASE_URL}/agentbalance/invitesummaryclient/${uid}`,
      );
      setFriendInvite(res?.data?.data || []);
    } catch (e) {
      // silent
    }
  }, [uid]);

  const fetchInvitedFriends = useCallback(async () => {
    if (!uid) return;
    try {
      const res = await axios.get(
        `${API_BASE_URL}/agentbalance/invitedfriendsclient/${uid}`,
      );
      const rows = Array.isArray(res?.data?.data)
        ? res.data.data
        : Array.isArray(res?.data)
          ? res.data
          : [];
      const total = Number(res?.data?.summary?.total || rows.length || 0);
      setInvitedFriends(rows);
      setInvitedFriendsSummary({
        total,
      });
      setInvitedFriendsError("");
    } catch (e) {
      setInvitedFriends([]);
      setInvitedFriendsSummary({ total: 0 });
      setInvitedFriendsError("ไม่สามารถโหลดรายชื่อเพื่อนที่เชิญได้");
    }
  }, [uid]);

  const maskUsername = useCallback((username) => {
    const safeUsername = String(username || "");
    return `xxxx${safeUsername.slice(-4)}`;
  }, []);

  const claimInviteBonus = useCallback(
    async (inviteId) => {
      if (!uid || !inviteId) return;
      setLoadingId(inviteId);
      setMessage("");
      try {
        const res = await axios.post(
          `${API_BASE_URL}/agentbalance/paidinvite/${uid}`,
          { data: inviteId },
        );
        if (res?.data?.success === true) {
          setMessage("รับโบนัสเชิญเพื่อนเรียบร้อยแล้ว");
          setMessageType("success");
          try {
            dispatch(getPlayerBalance({ id: uid }));
          } catch (_) {}
          await fetchFriendInvite();
        } else {
          setMessage("กรุณาเคลียร์เทิร์นก่อนรับโบนัสเชิญเพื่อน");
          setMessageType("error");
        }
      } catch (e) {
        setMessage("ไม่สามารถรับโบนัสเชิญเพื่อนได้ กรุณาติดต่อเจ้าหน้าที่");
        setMessageType("error");
      } finally {
        setLoadingId(null);
      }
    },
    [uid, fetchFriendInvite, dispatch],
  );

  useEffect(() => {
    fetchInviteSetting();
    fetchFriendInvite();
    fetchInvitedFriends();
  }, [fetchInviteSetting, fetchFriendInvite, fetchInvitedFriends]);

  const unreceived = friendInvite.filter((f) => f.inviteStatus === "UNRECEIVE");
  const totalUnreceivedIncome = useMemo(
    () => unreceived.reduce((sum, row) => sum + Number(row?.amount || 0), 0),
    [unreceived],
  );

  return (
    <div className={styles.wrapper}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.iconWrapper}>
          <svg
            className={styles.icon}
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M16 4V2C16 1.44772 15.5523 1 15 1H9C8.44772 1 8 1.44772 8 2V4M16 4H8M16 4C17.1046 4 18 4.89543 18 6V10M8 4C6.89543 4 6 4.89543 6 6V10M18 10V20C18 21.1046 17.1046 22 16 22H8C6.89543 22 6 21.1046 6 20V10M18 10H6M10 9V13M14 9V13"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <h4 className={styles.title}>🤝 โปรแกรมการแนะนำเพื่อน</h4>
        <p className={styles.subtitle}>
          แนะนำเพื่อนและรับรายได้พิเศษอย่างต่อเนื่อง
        </p>
      </div>

      <div className={styles.mainTabs}>
        {[
          { id: "overview", label: "ข้อมูลภาพรวม" },
          { id: "report", label: "รายงานการแนะนำ" },
          { id: "friends", label: "เพื่อนที่เชิญ" },
          { id: "income", label: "รายได้และผลตอบแทน" },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            className={`${styles.mainTabBtn} ${
              mainTab === t.id ? styles.active : ""
            }`}
            onClick={() => setMainTab(t.id)}
          >
            {t.label}
            {mainTab === t.id && <span className={styles.underline} />}
          </button>
        ))}
      </div>

      {/* Overview */}
      {mainTab === "overview" && (
        <div className={styles.overviewSection}>
          <div className={styles.revenueCard}>
            <div className={styles.revLabel}>รายได้รวม</div>
            <div className={styles.revAmount}>
              {totalUnreceivedIncome.toLocaleString("th-TH", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}{" "}
              THB
            </div>
          </div>

          <div className={styles.linkGroup}>
            <div className={styles.linkLabel}>ลิงก์สำหรับแนะนำเพื่อน</div>
            <div className={styles.linkInputWrap}>
              <input
                readOnly
                value={referralLink}
                className={styles.linkInput}
              />
              <button type="button" onClick={copy} className={styles.copyBtn}>
                คัดลอก
              </button>
            </div>
            {inviteDetail ? (
              <div className={styles.linkDescription}>{inviteDetail}</div>
            ) : null}
          </div>

          {/* Unreceived bonus list + claim action */}
          <div className={styles.bonusSection}>
            <div className={styles.bonusHeader}>
              <h3>โบนัสที่ยังไม่ได้รับ</h3>
            </div>
            {unreceived.length === 0 ? (
              <div className={styles.emptyState}>
                <div className={styles.emptyIcon}>
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 8V12M12 16H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <p style={{ color: "black" }}>ยังไม่มีโบนัสเชิญเพื่อน</p>
              </div>
            ) : (
              <div className={styles.bonusGrid}>
                {unreceived.map((row) => (
                  <div key={row.id} className={styles.bonusCard}>
                    <div className={styles.bonusInfo}>
                      <div className={styles.bonusUser}>{row?.username}</div>
                      <div className={styles.bonusDate}>
                        {moment(row?.created_at)
                          .add(543, "year")
                          .format("DD-MM-YYYY HH:mm")}
                      </div>
                    </div>
                    <div className={styles.bonusActions}>
                      <div className={styles.bonusAmount}>
                        {row?.amount} เครดิต
                      </div>
                      <button
                        type="button"
                        className={styles.claimBtn}
                        disabled={loadingId === row.id}
                        onClick={() => claimInviteBonus(row.id)}
                        title="รับโบนัสเชิญเพื่อน"
                      >
                        {loadingId === row.id ? "กำลังรับ..." : "รับโบนัส"}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {withdrawDetail ? (
              <div className={styles.withdrawDetail}>
                รายละเอียดเทิร์น : {withdrawDetail}
              </div>
            ) : null}
            {message ? (
              <div className={`${styles.message} ${styles[messageType]}`}>
                {message}
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* Report */}
      {mainTab === "report" && (
        <div className={styles.reportSection}>
          <div className={styles.dateFilters}>
            <div className={styles.dateField}>
              <span>วันที่เริ่มต้น :</span>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className={styles.dateField}>
              <span>วันที่สิ้นสุด :</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>
          <div className={styles.table}>
            <div className={styles.tableHead}>
              <div>ชื่อผู้ใช้</div>
              <div>ประเภท</div>
              <div>วันที่</div>
              <div className={styles.right}>จำนวน</div>
            </div>
            {friendInvite.length === 0 ? (
              <div className={styles.emptyCell}>ไม่มีรายการ</div>
            ) : (
              <div className={styles.tableBody}>
                {friendInvite
                  .slice()
                  .sort(
                    (a, b) => new Date(b.created_at) - new Date(a.created_at),
                  )
                  .map((row) => (
                    <div key={row.id} className={styles.tableRow}>
                      <div>{row.username}</div>
                      <div>
                        {row.inviteStatus === "UNRECEIVE" ? "รอรับ" : "สำเร็จ"}
                      </div>
                      <div>
                        {moment(row.created_at)
                          .add(543, "year")
                          .format("DD-MM-YYYY : HH:mm")}
                      </div>
                      <div className={styles.right}>{row.amount}</div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>
      )}

      {mainTab === "friends" && (
        <div className={styles.reportSection}>
          <div className={styles.summaryGrid}>
            <div className={styles.summaryCard}>
              <div className={styles.summaryLabel}>จำนวนเพื่อนที่เชิญ</div>
              <div className={styles.summaryValue}>
                {invitedFriendsSummary.total.toLocaleString("th-TH")} คน
              </div>
            </div>
          </div>

          {invitedFriendsError ? (
            <div className={`${styles.message} ${styles.error}`}>
              {invitedFriendsError}
            </div>
          ) : null}

          <div className={`${styles.table} ${styles.desktopFriendsTable}`}>
            <div className={`${styles.tableHead} ${styles.tableHeadFriends}`}>
              <div>ลำดับ</div>
              <div>username</div>
              <div>วันที่สมัคร</div>
            </div>
            {invitedFriends.length === 0 ? (
              <div className={styles.emptyCell}>ไม่มีรายการ</div>
            ) : (
              <div className={`${styles.tableBody} ${styles.tableBodyFriends}`}>
                {invitedFriends.map((row, index) => (
                  <div
                    key={row.id}
                    className={`${styles.tableRow} ${styles.tableRowFriends}`}
                  >
                    <div>{index + 1}</div>
                    <div>{maskUsername(row.username)}</div>
                    <div>
                      {moment(row.created_at)
                        .add(543, "year")
                        .format("DD-MM-YYYY : HH:mm")}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {invitedFriends.length === 0 ? null : (
            <div className={styles.friendsCardList}>
              {invitedFriends.map((row, index) => (
                <div key={row.id} className={styles.friendCard}>
                  <div className={styles.friendCardHeader}>
                    <div className={styles.friendCardIndex}>#{index + 1}</div>
                    <div className={styles.friendCardUsername}>
                      {maskUsername(row.username)}
                    </div>
                  </div>
                  <div className={styles.friendCardMeta}>
                    <span className={styles.friendCardLabel}>วันที่สมัคร</span>
                    <span className={styles.friendCardValue}>
                      {moment(row.created_at)
                        .add(543, "year")
                        .format("DD-MM-YYYY HH:mm")}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Income */}
      {mainTab === "income" && (
        <div className={styles.incomeSection}>
          <div className={styles.incomeTabs}>
            <button
              type="button"
              className={`${styles.incomeTabBtn} ${
                incomeTab === "income" ? styles.incomeActive : ""
              }`}
              onClick={() => setIncomeTab("income")}
            >
              รายได้รายเดือน
            </button>
            <button
              type="button"
              className={`${styles.incomeTabBtn} ${
                incomeTab === "history" ? styles.incomeActive : ""
              }`}
              onClick={() => setIncomeTab("history")}
            >
              ประวัติการรับเงิน
            </button>
          </div>

          {incomeTab === "income" && (
            <div className={styles.table}>
              <div className={styles.tableHead}>
                <div>วันเวลา</div>
                <div>ช่องทาง</div>
                <div className={styles.right}>จำนวน</div>
              </div>
              <div className={styles.emptyCell}>ไม่มีรายการ</div>
            </div>
          )}

          {incomeTab === "history" && (
            <div className={styles.revenueCardWide}>
              <div className={styles.revLabel}>รายได้ทั้งหมด</div>
              <div className={styles.revAmount}>0.00 THB</div>
              <button
                type="button"
                className={styles.withdrawBtn}
                disabled
                title="mock"
              >
                ถอนเข้ากระเป๋าเงิน
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
